import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import {
  ButtonBase,
  Icon,
  InputBase,
  Popover,
  TextField,
} from '@material-ui/core'
import { alpha, makeStyles } from '@material-ui/core/styles'
import HttpHandler from 'app/reusable/HttpHandler'
import { FILTER_TYPE, isFilterActive, summarizeFilter } from './listQuery'

const useStyles = makeStyles(({ palette, shadows }) => ({
  chip: {
    flexShrink: 0,
    height: 32,
    padding: '0 10px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    gap: 6,
    color: palette.text.secondary,
    border: `1px dashed ${palette.divider}`,
    transition: 'background-color 150ms ease-in',
    '&:hover': { backgroundColor: palette.action.hover },
    '& .material-icons': { fontSize: 16 },
  },
  chipActive: {
    color: palette.primary.contrastText,
    backgroundColor: palette.primary.main,
    border: `1px solid ${palette.primary.main}`,
    '&:hover': { backgroundColor: palette.primary.dark },
  },
  chipSummary: {
    fontWeight: 400,
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  paper: {
    width: 300,
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: 12,
    padding: 12,
    boxShadow: shadows[8],
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  title: { margin: 0, fontSize: 15, fontWeight: 700 },
  clear: {
    fontSize: 13,
    fontWeight: 600,
    color: palette.primary.main,
    padding: '2px 4px',
    borderRadius: 4,
    '&:disabled': { color: palette.text.disabled },
  },
  segmented: {
    display: 'flex',
    padding: 4,
    borderRadius: 10,
    backgroundColor: palette.action.hover,
    marginBottom: 10,
  },
  segment: {
    flex: 1,
    height: 32,
    borderRadius: 8,
    fontSize: 13,
    color: palette.text.secondary,
  },
  segmentSelected: {
    fontWeight: 700,
    color: palette.text.primary,
    backgroundColor: palette.background.paper,
    boxShadow: shadows[1],
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 8,
    marginBottom: 6,
    backgroundColor: palette.action.hover,
    '& .material-icons': { fontSize: 18, color: palette.text.secondary },
  },
  options: {
    maxHeight: 280,
    overflowY: 'auto',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  option: {
    width: '100%',
    justifyContent: 'space-between',
    padding: '8px 10px',
    borderRadius: 8,
    fontSize: 14,
    textAlign: 'left',
    '&:hover': { backgroundColor: palette.action.hover },
  },
  optionSelected: {
    backgroundColor: alpha(palette.primary.main, 0.1),
    '&:hover': { backgroundColor: alpha(palette.primary.main, 0.16) },
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '1px 8px',
    borderRadius: 6,
    fontSize: 13,
    border: `1px solid ${palette.divider}`,
    backgroundColor: palette.background.paper,
  },
  dot: { width: 8, height: 8, borderRadius: 2, flexShrink: 0 },
  check: { color: palette.primary.main, fontSize: 18 },
  empty: {
    padding: '12px 10px',
    fontSize: 13,
    color: palette.text.secondary,
  },
  dateFields: { display: 'flex', gap: 8, marginBottom: 10 },
  presets: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  apply: {
    width: '100%',
    height: 34,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: palette.primary.contrastText,
    backgroundColor: palette.primary.main,
    '&:hover': { backgroundColor: palette.primary.dark },
  },
  preset: {
    fontSize: 12,
    padding: '4px 10px',
    borderRadius: 999,
    border: `1px solid ${palette.divider}`,
    '&:hover': { backgroundColor: palette.action.hover },
  },
}))

const SEARCHABLE_OPTIONS_THRESHOLD = 6

const OptionList = ({ options, isSelected, onToggle, emptyText }) => {
  const classes = useStyles()

  if (!options.length) return <p className={classes.empty}>{emptyText}</p>

  return (
    <ul className={classes.options} role="listbox" aria-multiselectable>
      {options.map((option) => {
        const selected = isSelected(option.value)
        return (
          <li key={option.value} role="none">
            <ButtonBase
              role="option"
              aria-selected={selected}
              className={clsx(
                classes.option,
                selected && classes.optionSelected
              )}
              onClick={() => onToggle(option)}
            >
              <span className={classes.tag}>
                {!!option.color && (
                  <span
                    className={classes.dot}
                    style={{ backgroundColor: option.color }}
                  />
                )}
                {option.label}
              </span>
              {selected && (
                <Icon className={classes.check} aria-hidden>
                  check
                </Icon>
              )}
            </ButtonBase>
          </li>
        )
      })}
    </ul>
  )
}

const SearchInput = ({ value, onChange, placeholder }) => {
  const classes = useStyles()
  return (
    <div className={classes.search}>
      <Icon aria-hidden>search</Icon>
      <InputBase
        autoFocus
        fullWidth
        value={value}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

const normalize = (text) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

const MultiFilter = ({ definition, value, onChange }) => {
  const classes = useStyles()
  const [query, setQuery] = useState('')
  // Remembers "Excluir" picked before any option, so the first option
  // picked is already excluded.
  const [pendingMode, setPendingMode] = useState('include')
  const mode = value?.mode || pendingMode
  const values = value?.values || []
  const options = definition.options

  const visibleOptions = useMemo(
    () =>
      (options || []).filter((option) =>
        normalize(option.label).includes(normalize(query))
      ),
    [options, query]
  )

  const toggle = (option) => {
    const next = values.includes(option.value)
      ? values.filter((v) => v !== option.value)
      : [...values, option.value]
    onChange(next.length ? { mode, values: next } : undefined)
  }

  return (
    <>
      {definition.allowExclude !== false && (
        <div className={classes.segmented} role="group">
          {[
            ['include', 'Incluir'],
            ['exclude', 'Excluir'],
          ].map(([segment, label]) => (
            <ButtonBase
              key={segment}
              aria-pressed={mode === segment}
              className={clsx(
                classes.segment,
                mode === segment && classes.segmentSelected
              )}
              onClick={() => {
                setPendingMode(segment)
                if (values.length) onChange({ mode: segment, values })
              }}
            >
              {label}
            </ButtonBase>
          ))}
        </div>
      )}
      {(options || []).length >= SEARCHABLE_OPTIONS_THRESHOLD && (
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder={`Buscar ${definition.label.toLowerCase()}`}
        />
      )}
      <OptionList
        options={visibleOptions}
        isSelected={(v) => values.includes(v)}
        onToggle={toggle}
        emptyText="Nenhuma opção encontrada"
      />
    </>
  )
}

const BooleanFilter = ({ definition, value, onChange }) => (
  <OptionList
    options={[
      { value: true, label: definition.trueLabel || 'Sim' },
      { value: false, label: definition.falseLabel || 'Não' },
    ]}
    isSelected={(v) => value === v}
    onToggle={(option) =>
      onChange(value === option.value ? undefined : option.value)
    }
  />
)

const MODEL_SEARCH_DEBOUNCE_MS = 300

const ModelFilter = ({ definition, value, onChange }) => {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState(null)

  useEffect(() => {
    let cancelled = false
    const timeout = setTimeout(() => {
      HttpHandler.list(definition.djangoApp, definition.djangoModel, {
        select_return: true,
        paginate_by: 8,
        text_filter: query || undefined,
        ...(definition.optionFilters || {}),
      })
        .then(({ data }) => !cancelled && setOptions(data?.data || []))
        .catch(() => !cancelled && setOptions([]))
    }, MODEL_SEARCH_DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  const values = useMemo(() => value?.values || [], [value])
  const labels = useMemo(() => value?.labels || {}, [value])

  // Picked records stay listed (first) even when the search no longer
  // matches them, so they can be unpicked.
  const visibleOptions = useMemo(() => {
    const found = (options || []).map((option) => ({
      ...option,
      value: String(option.value),
    }))
    const picked = values
      .filter((id) => !found.some((option) => option.value === id))
      .map((id) => ({ value: id, label: labels[id] || '…' }))
    return [...picked, ...found]
  }, [options, values, labels])

  const toggle = (option) => {
    const isPicked = values.includes(option.value)
    let next
    if (isPicked) next = values.filter((id) => id !== option.value)
    else next = definition.multiple ? [...values, option.value] : [option.value]

    onChange(
      next.length
        ? {
            values: next,
            labels: { ...labels, [option.value]: option.label },
          }
        : undefined
    )
  }

  return (
    <>
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={`Buscar ${definition.label.toLowerCase()}`}
      />
      <OptionList
        options={visibleOptions}
        isSelected={(v) => values.includes(v)}
        onToggle={toggle}
        emptyText={options === null ? 'Carregando…' : 'Nenhum resultado'}
      />
    </>
  )
}

const ChoiceFilter = ({ definition, value, onChange }) => (
  <OptionList
    options={definition.options || []}
    isSelected={(v) => value === v}
    onToggle={(option) =>
      onChange(value === option.value ? undefined : option.value)
    }
  />
)

const NumberRangeFilter = ({ definition, value, onChange }) => {
  const classes = useStyles()
  const [draft, setDraft] = useState({
    min: value?.min ?? '',
    max: value?.max ?? '',
  })

  const toNumber = (raw) =>
    raw === '' || raw === null || !Number.isFinite(Number(raw))
      ? undefined
      : Number(raw)

  const apply = () => {
    const min = toNumber(draft.min)
    const max = toNumber(draft.max)
    onChange(min === undefined && max === undefined ? undefined : { min, max })
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        apply()
      }}
    >
      <div className={classes.dateFields}>
        {[
          ['min', 'Mínimo'],
          ['max', 'Máximo'],
        ].map(([field, label]) => (
          <TextField
            key={field}
            id={`filter-${definition.key}-${field}`}
            type="number"
            label={label}
            size="small"
            variant="outlined"
            value={draft[field]}
            inputProps={{ step: definition.step || 'any', min: 0 }}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, [field]: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
        ))}
      </div>
      <ButtonBase type="submit" className={classes.apply}>
        Aplicar
      </ButtonBase>
    </form>
  )
}

export const DATE_FORMAT = 'YYYY-MM-DD'

const DATE_PRESETS = [
  {
    label: 'Últimos 7 dias',
    range: () => ({
      from: moment().subtract(6, 'days').format(DATE_FORMAT),
      to: moment().format(DATE_FORMAT),
    }),
  },
  {
    label: 'Últimos 30 dias',
    range: () => ({
      from: moment().subtract(29, 'days').format(DATE_FORMAT),
      to: moment().format(DATE_FORMAT),
    }),
  },
  {
    label: 'Este mês',
    range: () => ({
      from: moment().startOf('month').format(DATE_FORMAT),
      to: moment().endOf('month').format(DATE_FORMAT),
    }),
  },
  {
    label: 'Este ano',
    range: () => ({
      from: moment().startOf('year').format(DATE_FORMAT),
      to: moment().endOf('year').format(DATE_FORMAT),
    }),
  },
]

const DateRangeFilter = ({ definition, value, onChange }) => {
  const classes = useStyles()

  const update = (field, date) => {
    const next = { ...(value || {}), [field]: date || undefined }
    onChange(next.from || next.to ? next : undefined)
  }

  return (
    <>
      <div className={classes.dateFields}>
        {[
          ['from', 'De'],
          ['to', 'Até'],
        ].map(([field, label]) => (
          <TextField
            key={field}
            id={`filter-${definition.key}-${field}`}
            type="date"
            label={label}
            size="small"
            variant="outlined"
            value={value?.[field] || ''}
            onChange={(e) => update(field, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        ))}
      </div>
      <div className={classes.presets}>
        {(definition.presets || DATE_PRESETS).map((preset) => (
          <ButtonBase
            key={preset.label}
            className={classes.preset}
            onClick={() => onChange(preset.range())}
          >
            {preset.label}
          </ButtonBase>
        ))}
      </div>
    </>
  )
}

const FILTER_COMPONENTS = {
  [FILTER_TYPE.MULTI]: MultiFilter,
  [FILTER_TYPE.CHOICE]: ChoiceFilter,
  [FILTER_TYPE.NUMBER_RANGE]: NumberRangeFilter,
  [FILTER_TYPE.BOOLEAN]: BooleanFilter,
  [FILTER_TYPE.MODEL]: ModelFilter,
  [FILTER_TYPE.DATE_RANGE]: DateRangeFilter,
}

/**
 * One filter of a `ModelListPage`: a chip that reads "+ Label" when unset
 * and "Label: summary" when set, opening a popover to edit the value.
 * Changes apply immediately.
 */
const FilterChip = ({ definition, value, onChange }) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const active = isFilterActive(definition, value)
  const FilterComponent = FILTER_COMPONENTS[definition.type]

  return (
    <>
      <ButtonBase
        className={clsx(
          't-filter-chip',
          classes.chip,
          active && classes.chipActive
        )}
        aria-haspopup="dialog"
        aria-expanded={!!anchorEl}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Icon aria-hidden>
          {active ? definition.icon || 'filter_list' : 'add'}
        </Icon>
        <span>{active ? `${definition.label}:` : definition.label}</span>
        {active && (
          <span className={classes.chipSummary}>
            {summarizeFilter(definition, value)}
          </span>
        )}
        {active && <Icon aria-hidden>expand_more</Icon>}
      </ButtonBase>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: -6, horizontal: 'left' }}
        classes={{ paper: classes.paper }}
        PaperProps={{ role: 'dialog', 'aria-label': definition.label }}
      >
        <div className={classes.header}>
          <h6 className={classes.title}>{definition.label}</h6>
          <ButtonBase
            className={classes.clear}
            disabled={!active}
            onClick={() => onChange(undefined)}
          >
            Limpar
          </ButtonBase>
        </div>
        <FilterComponent
          definition={definition}
          value={value}
          onChange={onChange}
        />
      </Popover>
    </>
  )
}

export default FilterChip
